import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["card", "column", "columnBody", "count", "empty", "stageBadge"]
  static values = { emptyColumnText: String, statusLabels: Object, funnelLabels: Object }

  connect() {
    this.dragging = false
    this.justDragged = false
    this.dragResetTimer = null
  }

  disconnect() {
    window.clearTimeout(this.dragResetTimer)
  }

  dragStart(event) {
    const card = event.currentTarget
    this.dragging = true
    this.draggingCard = card

    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", card.dataset.inquiryId)
    card.classList.add("opacity-50")
  }

  dragEnd() {
    if (this.draggingCard) {
      this.draggingCard.classList.remove("opacity-50")
      this.draggingCard = null
    }

    this.dragging = false
    this.justDragged = true
    window.clearTimeout(this.dragResetTimer)
    this.dragResetTimer = window.setTimeout(() => {
      this.justDragged = false
    }, 100)
  }

  visitCard(event) {
    if (this.dragging || this.justDragged) {
      event.preventDefault()
    }
  }

  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    event.currentTarget.classList.add("ring-2", "ring-primary/30")
  }

  dragLeave(event) {
    event.currentTarget.classList.remove("ring-2", "ring-primary/30")
  }

  async drop(event) {
    event.preventDefault()
    const targetColumn = event.currentTarget
    targetColumn.classList.remove("ring-2", "ring-primary/30")

    const inquiryId = event.dataTransfer.getData("text/plain")
    const status = targetColumn.dataset.status
    const card = this.cardTargets.find((node) => node.dataset.inquiryId === inquiryId)
    if (!card || !status || card.dataset.status === status) return

    const url = card.dataset.updateUrl
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (!url || !token) return

    const previousStatus = card.dataset.status
    const previousLabel = card.dataset.stageLabel

    this.moveCard(card, targetColumn)

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": token,
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: new URLSearchParams({ status }),
        credentials: "same-origin"
      })

      if (!response.ok) throw new Error("status update failed")

      const payload = await response.json()
      if (payload.stage_label) {
        this.setStageBadge(card, payload.stage_label, payload.funnel_stage)
      }
    } catch {
      const revertColumn = this.columnTargets.find((column) => column.dataset.status === previousStatus)
      if (revertColumn) this.moveCard(card, revertColumn)
      if (previousLabel) this.setStageBadge(card, previousLabel, card.dataset.funnelStage)
      card.classList.add("ring-2", "ring-negative/40")
      window.setTimeout(() => card.classList.remove("ring-2", "ring-negative/40"), 1200)
    }
  }

  moveCard(card, column) {
    const body = column.querySelector("[data-inbox-kanban-target='columnBody']")
    if (!body) return

    const empty = body.querySelector("[data-inbox-kanban-target='empty']")
    if (empty) empty.remove()

    const sourceColumn = card.closest("[data-inbox-kanban-target='column']")
    body.appendChild(card)
    card.dataset.status = column.dataset.status
    card.classList.remove("opacity-50", "opacity-40", "pointer-events-none")

    const status = column.dataset.status
    const statusLabel = this.statusLabelsValue[status]
    if (statusLabel) this.setStageBadge(card, statusLabel, status)

    if (sourceColumn && sourceColumn !== column) {
      this.syncColumn(sourceColumn)
    }
    this.syncColumn(column)
  }

  setStageBadge(card, label, funnelStage) {
    const badge = card.querySelector("[data-inbox-kanban-target='stageBadge']")
    if (!badge || !label) return

    badge.textContent = label
    card.dataset.stageLabel = label
    if (funnelStage) card.dataset.funnelStage = funnelStage
  }

  syncColumn(column) {
    const body = column.querySelector("[data-inbox-kanban-target='columnBody']")
    if (!body) return

    const cards = body.querySelectorAll("[data-inbox-kanban-target='card']")
    const count = column.querySelector("[data-inbox-kanban-target='count']")
    if (count) count.textContent = String(cards.length)

    const hasEmpty = body.querySelector("[data-inbox-kanban-target='empty']")
    if (cards.length === 0 && !hasEmpty) {
      const placeholder = document.createElement("p")
      placeholder.setAttribute("data-inbox-kanban-target", "empty")
      placeholder.className = "m-0 px-1 py-6 text-center text-xs text-muted"
      placeholder.textContent = this.emptyColumnTextValue
      body.appendChild(placeholder)
    }
  }
}
